import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { ImagePlus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useHasRole } from '@/hooks/useSession';
import { uploadTransmitterImage, deleteTransmitterImage } from '../../api';
import type { TransmitterV2Document } from '../../types';

// =============================================================================
// Types
// =============================================================================

interface ImageUploadSectionProps {
  transmitter: TransmitterV2Document;
  onRefresh: () => void;
  canManageImages?: boolean;
}

interface ImageSlotProps {
  label: string;
  image: { _id: string; organisation: string; extension: string } | null;
  organisationId: string;
  isUploading: boolean;
  isDeleting: boolean;
  onUpload: (file: File) => void;
  onDelete: () => void;
  canManageImages: boolean;
}

// =============================================================================
// Helpers
// =============================================================================

function getImageUrl(organisationId: string, imageId: string): string {
  return `/api/v4/legacy/v2/files/${organisationId}/${imageId}`;
}

// =============================================================================
// Image Slot Component
// =============================================================================

function ImageSlot({
  label,
  image,
  organisationId,
  isUploading,
  isDeleting,
  onUpload,
  onDelete,
  canManageImages,
}: ImageSlotProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = React.useState(false);

  const isLoading = isUploading || isDeleting;

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid file type', { description: 'Please select an image file.' });
      return;
    }
    onUpload(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canManageImages && !isLoading) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!canManageImages || isLoading) return;

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect(file);
    }
  };

  const handleClick = () => {
    if (canManageImages && !isLoading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const imageUrl = image ? getImageUrl(organisationId, image._id) : null;

  return (
    <div className="flex-1 min-w-0">
      <Label className="text-xs text-muted-foreground mb-1.5 block">{label}</Label>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleInputChange}
        disabled={isLoading || !canManageImages}
      />

      <div
        onClick={!image ? handleClick : undefined}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative border-2 border-dashed rounded-lg transition-colors overflow-hidden',
          'flex items-center justify-center',
          'h-[200px]',
          image ? 'border-transparent' : 'border-muted-foreground/25',
          !image && canManageImages && 'hover:border-muted-foreground/50 cursor-pointer',
          dragActive && 'border-primary bg-primary/5',
          (isLoading || !canManageImages) && 'opacity-50 cursor-not-allowed',
        )}
      >
        {isLoading ? (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="text-xs">
              {isUploading ? 'Uploading...' : 'Deleting...'}
            </span>
          </div>
        ) : image && imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt={label}
              className="w-full h-full object-cover"
            />
            {canManageImages && (
              <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick();
                  }}
                >
                  <ImagePlus className="h-4 w-4 mr-1" />
                  Replace
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground p-4">
            <ImagePlus className="h-8 w-8" />
            <span className="text-xs text-center">
              {canManageImages ? 'Click or drag to upload' : 'No image'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Image Upload Section
// =============================================================================

export function ImageUploadSection({ transmitter, onRefresh, canManageImages = false }: ImageUploadSectionProps) {
  const isAdmin = useHasRole('full_admin');

  const uploadMutation1 = useMutation({
    mutationFn: (file: File) =>
      uploadTransmitterImage(transmitter._id, file, 1, isAdmin, transmitter.organisation),
    onSuccess: () => {
      toast.success('Image 1 uploaded');
      onRefresh();
    },
    onError: (error) => {
      toast.error('Failed to upload image', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const uploadMutation2 = useMutation({
    mutationFn: (file: File) =>
      uploadTransmitterImage(transmitter._id, file, 2, isAdmin, transmitter.organisation),
    onSuccess: () => {
      toast.success('Image 2 uploaded');
      onRefresh();
    },
    onError: (error) => {
      toast.error('Failed to upload image', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const deleteMutation1 = useMutation({
    mutationFn: () => {
      if (!transmitter.image?._id) throw new Error('No image to delete');
      return deleteTransmitterImage(transmitter._id, transmitter.image._id, 1, isAdmin);
    },
    onSuccess: () => {
      toast.success('Image 1 removed');
      onRefresh();
    },
    onError: (error) => {
      toast.error('Failed to remove image', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const deleteMutation2 = useMutation({
    mutationFn: () => {
      if (!transmitter.image2?._id) throw new Error('No image to delete');
      return deleteTransmitterImage(transmitter._id, transmitter.image2._id, 2, isAdmin);
    },
    onSuccess: () => {
      toast.success('Image 2 removed');
      onRefresh();
    },
    onError: (error) => {
      toast.error('Failed to remove image', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Images</Label>
      <div className="flex gap-4">
        <ImageSlot
          label="Image 1"
          image={transmitter.image}
          organisationId={transmitter.organisation}
          isUploading={uploadMutation1.isPending}
          isDeleting={deleteMutation1.isPending}
          onUpload={(file) => uploadMutation1.mutate(file)}
          onDelete={() => deleteMutation1.mutate()}
          canManageImages={canManageImages}
        />
        <ImageSlot
          label="Image 2"
          image={transmitter.image2}
          organisationId={transmitter.organisation}
          isUploading={uploadMutation2.isPending}
          isDeleting={deleteMutation2.isPending}
          onUpload={(file) => uploadMutation2.mutate(file)}
          onDelete={() => deleteMutation2.mutate()}
          canManageImages={canManageImages}
        />
      </div>
    </div>
  );
}
